<script setup lang="ts">
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { KsIcon } from '@aschehoug/kloss'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useImage from '~/composables/useImage'
import { useContentType } from '~/composables/useContentType'
import useContentApi from '~/api/contentApi'

const { section } = defineProps<{ section: ContentSectionStandalone }>()

const { buildResourceUrl } = useUrlBuilder()
const { getName } = useContentType()
const { findContents } = useContentApi()
const { getImageSrc, getImageAltText } = useImage()

const contentTypeName = computed(() => data?.value ? getName(data.value) : '')

const image = computed(() => ({
  src: Number(section.image?.destinationContentId) > 0 ? getImageSrc(section.image, 'thumbnail_large') : data.value?.thumbnail,
  alt: Number(section.image?.destinationContentId) > 0 ? getImageAltText(section.image) : ''
}))

const { data } = useQuery({
  staleTime: Infinity,
  enabled: Number(section.resource?.destinationContentId) > 0,
  queryKey: ['section-standalone', section.contentId],
  queryFn: async () => (await findContents({
    contentIdCriterion: [Number(section.resource.destinationContentId)],
    mainLocationCriterion: true,
  }, 1))[0] ?? null
})
</script>

<template>
  <article
    v-if="data"
    class="mx-auto size-full max-w-screen-au px-4 sm:px-8"
  >
    <a
      :href="buildResourceUrl(data)"
      class="
        group flex min-h-[300px] w-full flex-col overflow-hidden rounded-2xl
        border-2 border-gray-10 transition-all hover:shadow-lg focus-visible:ring sm:flex-row
      "
      :class="{'sm:flex-row-reverse': section.alignRight}"
    >
      <div
        class="aspect-video max-h-[250px] w-full min-w-full sm:max-h-full sm:min-w-0"
        :style="`background: ${section.color[0] ? section.color[0] : 'rgb(var(--au-gray-40))'}`"
      >
        <img
          v-if="image.src"
          :src="image.src"
          class="block size-full overflow-hidden object-cover object-center"
          :alt="image.alt"
        >
      </div>
      <div class="flex w-full flex-col justify-center bg-white p-6 transition-colors sm:p-10">
        <p
          v-if="contentTypeName"
          class="mb-4 font-semibold text-seagreen-40"
          v-text="contentTypeName"
        />
        <h2 class="mb-2 text-2xl font-bold text-blue-50 sm:text-3xl">
          <span class="relative block w-fit">
            {{ section.title }}
            <KsIcon
              icon="arrow-right"
              scale="0.7"
              class="mb-1 ml-1 inline-block transition-all group-hover:ml-2 group-focus-visible:ml-2"
            />
          </span>
        </h2>
        <p
          class="text-base text-gray-40 md:w-5/6"
          v-html="section.description"
        />
      </div>
    </a>
  </article>
</template>
